/**
 * RemoteDataSource — HTTPS + WebSocket implementation for the web shell.
 *
 * **Status: scaffolding for the web migration. Not yet wired to a real server.**
 *
 * Channel-string requests become HTTP POSTs against the API server; push
 * events arrive over a WebSocket subscription. The server-side endpoint
 * shape mirrors the IPC channels 1:1 — see latest/pattern-c-migration-plan.md
 * for the channel-to-route mapping.
 *
 * For Phase 1 (single-tenant, narrow CRUD), only a handful of channels need
 * real implementations on the server. The web shell can `invoke` any channel
 * and get back either a real response (if the route exists) or a NOT_IMPLEMENTED
 * error (clear feedback during development).
 *
 * Auth: a token getter is injected at construction time. The token is fetched
 * fresh on every request — that lets the shell's auth state (OIDC session)
 * drive without RemoteDataSource needing to listen to login/logout events.
 */

import type { DataSource } from './types.js';
import { IpcError } from './types.js';

/**
 * Optional hook for the DebugPanel (packages/ui) to receive every API
 * call this data-source dispatches. Wired by the app shell on startup
 * via setApiCallObserver(). The data-source layer has no compile-time
 * dependency on the UI layer — this is a thin observer-pattern bridge.
 *
 * Per latest/ENGINEERING-PRACTICES.md "API ledger" requirement.
 */
type ApiCallObserver = (entry: {
  channel: string;
  path: string;
  payload: unknown;
  response?: unknown;
  error?: { name: string; message: string; code?: string };
  requestId: string;
  durationMs: number;
  ok: boolean;
  status?: number;
  startedAt: string;
}) => void;

let _apiCallObserver: ApiCallObserver | null = null;

/**
 * Register a callback that receives every successful or failed API
 * call. Pass null to clear. Safe to call before any data source is
 * constructed. The shell wires this on startup to feed the
 * DebugPanel's API tab.
 */
export function setApiCallObserver(observer: ApiCallObserver | null): void {
  _apiCallObserver = observer;
}

export interface RemoteDataSourceOptions {
  /** Base URL of the API server, e.g. "https://localhost" or "https://api.cipherops.example". */
  baseUrl: string;

  /**
   * Returns the current auth token (OIDC access token / session cookie / JWT).
   * Called fresh on every request so token rotation is transparent.
   * Return null if the shell has no active session — invoke() will fail
   * fast with a SESSION_REQUIRED error.
   */
  getAuthToken: () => string | null | Promise<string | null>;

  /**
   * Optional WebSocket URL for push events. Defaults to baseUrl with the
   * protocol swapped to wss:// (or ws:// for plain http baseUrls).
   */
  wsUrl?: string;
}

export class RemoteDataSource implements DataSource {
  private opts: RemoteDataSourceOptions;
  private socket: WebSocket | null = null;
  private subscribers = new Map<string, Set<(payload: unknown) => void>>();

  constructor(options: RemoteDataSourceOptions) {
    this.opts = options;
  }

  async invoke<T = unknown>(channel: string, payload: unknown = {}): Promise<T> {
    // Channel "cipherops/clients/list" becomes path "/api/v1/clients/list".
    // Once the server is real, individual routes may use REST conventions
    // (GET /api/v1/clients) — but for the channel-shaped boundary, every
    // call is a POST to /api/v1/{channel-tail}.
    const path = channelToPath(channel);
    const url = `${this.opts.baseUrl}${path}`;

    // Auth header is sent when a token is available. Public endpoints
    // (/health, /api/v1/echo) work without; protected endpoints will reject
    // with SESSION_REQUIRED at the server boundary.
    const token = await this.opts.getAuthToken();

    // Fresh request id per invoke — see apps/server/REQUEST-ID.md.
    // Generated client-side so a bug report can carry the id even if the
    // request never reached the server. The server middleware will trust
    // and echo it back; in the unlikely case the echo is missing (e.g. an
    // edge proxy strips headers) we fall back to the id we sent.
    const requestId = generateRequestId();
    const startedAt = new Date().toISOString();
    const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Request-Id': requestId,
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    let res: Response;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload ?? {}),
        credentials: 'include',
      });
    } catch (err) {
      const durationMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0;
      // Network error — record before throwing so the Debug Panel sees it
      _apiCallObserver?.({
        channel, path, payload, requestId, durationMs, ok: false, startedAt,
        error: {
          name: err instanceof Error ? err.name : 'NetworkError',
          message: err instanceof Error ? err.message : 'Network request failed',
          code: 'NETWORK_ERROR',
        },
      });
      throw new IpcError(
        err instanceof Error ? err.message : 'Network request failed',
        'NETWORK_ERROR',
        undefined,
        requestId,
      );
    }

    // Read the echoed id (falls back to the one we sent so callers always
    // get a non-empty string).
    const echoedId = res.headers.get('X-Request-Id') ?? requestId;

    if (!res.ok) {
      let body: { code?: string; message?: string; details?: unknown } = {};
      try {
        body = await res.json();
      } catch {
        // Non-JSON error body — fall through with HTTP status only.
      }
      const durationMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0;
      _apiCallObserver?.({
        channel, path, payload, requestId: echoedId, durationMs, ok: false, status: res.status, startedAt,
        error: {
          name: 'IpcError',
          message: body.message ?? `HTTP ${res.status} ${res.statusText}`,
          code: body.code ?? `HTTP_${res.status}`,
        },
      });
      // Session died mid-visit — announce it once, globally.
      //
      // The server can end a session between one request and the next: the
      // account is deactivated, or the whole workspace is closed (a reclaimed
      // trial, a cancelled subscription, an organisation deleted upstream).
      // Without this, every screen independently caught its own 401 and
      // rendered an inline error, so the app CHROME stayed up — sidebar, firm
      // name, "FIRM ADMIN" — around empty content. It looked like you were
      // still signed in and the data had vanished, which is the wrong and
      // slightly alarming reading. Observed after an organisation deletion.
      //
      // One event, dispatched from the single place every API call funnels
      // through, lets the shell drop to the sign-in view no matter which screen
      // happened to make the call.
      // EXCLUDE the session probe. `auth/me` answers "are you signed in?" and
      // 401 is its normal NEGATIVE answer, not an event. Treating it as one
      // created an infinite reload loop on the signed-out splash: probe → 401
      // → "session ended" → reload → probe → … Anyone not signed in saw the
      // page refresh forever. A 401 only means a session ENDED if a session
      // was in play, which is never true for this channel.
      const isSessionProbe = channel === 'cipherops/auth/me';
      if (res.status === 401 && !isSessionProbe && typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('cipherops:session-ended', {
            detail: { code: body.code ?? 'SESSION_REQUIRED', message: body.message },
          }),
        );
      }

      throw new IpcError(
        body.message ?? `HTTP ${res.status} ${res.statusText}`,
        body.code ?? `HTTP_${res.status}`,
        body.details,
        echoedId,
      );
    }

    const response = (await res.json()) as T;
    const durationMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0;
    _apiCallObserver?.({
      channel, path, payload, response, requestId: echoedId, durationMs, ok: true, status: res.status, startedAt,
    });
    return response;
  }

  onPush(event: string, handler: (payload: unknown) => void): () => void {
    // Lazily open the WebSocket on first subscription.
    if (!this.socket) {
      this.openSocket();
    }
    let set = this.subscribers.get(event);
    if (!set) {
      set = new Set();
      this.subscribers.set(event, set);
      // Tell server we want this event stream.
      this.socket?.send(JSON.stringify({ type: 'subscribe', event }));
    }
    set.add(handler);

    return () => {
      const s = this.subscribers.get(event);
      if (!s) return;
      s.delete(handler);
      if (s.size === 0) {
        this.subscribers.delete(event);
        this.socket?.send(JSON.stringify({ type: 'unsubscribe', event }));
      }
    };
  }

  private openSocket(): void {
    const wsUrl =
      this.opts.wsUrl ?? this.opts.baseUrl.replace(/^http/, 'ws') + '/ws';
    try {
      this.socket = new WebSocket(wsUrl);
    } catch (err) {
      // Fail silently — push events are best-effort. Re-throwing here would
      // break the consumer's first onPush call, even though the request itself
      // is fine.
      console.warn('[RemoteDataSource] Failed to open WebSocket:', err);
      return;
    }

    this.socket.addEventListener('message', (msg) => {
      let parsed: { event?: string; payload?: unknown };
      try {
        parsed = JSON.parse(msg.data);
      } catch {
        return;
      }
      const { event, payload } = parsed;
      if (!event) return;
      const set = this.subscribers.get(event);
      if (!set) return;
      for (const handler of set) {
        try {
          handler(payload);
        } catch (err) {
          console.error('[RemoteDataSource] push handler threw:', err);
        }
      }
    });

    this.socket.addEventListener('close', () => {
      // Reset so the next onPush call reopens the socket.
      this.socket = null;
    });
  }
}

/**
 * Convert an IPC channel name into the API path the server will route on.
 * "cipherops/clients/list" → "/api/v1/clients/list"
 *
 * Exposed as a stable mapping so server code and migration tests can share it.
 */
export function channelToPath(channel: string): string {
  const trimmed = channel.startsWith('cipherops/') ? channel.slice('cipherops/'.length) : channel;
  return `/api/v1/${trimmed}`;
}

/**
 * Produce a fresh request id for an outgoing fetch. Prefers the browser's
 * native `crypto.randomUUID()` (Chromium, Firefox, Safari 15.4+, Node 19+).
 * Falls back to a 16-byte hex string built from `crypto.getRandomValues`
 * for ancient runtimes that don't expose randomUUID. Last-resort fallback
 * uses Math.random — collision-prone but the id still functions as a
 * correlation marker for that single request.
 */
function generateRequestId(): string {
  const g = (globalThis as { crypto?: Crypto }).crypto;
  if (g && typeof g.randomUUID === 'function') {
    return g.randomUUID();
  }
  if (g && typeof g.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    g.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  // Last-resort fallback. Never expected to run in modern browsers.
  return 'rid-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
